import { memo, useEffect } from 'react';
import { IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';

import styles from './AudioWave.module.scss';

export type AudioWaveProps = {
  track?: IMicrophoneAudioTrack;
};

export type AnimatedBarProps = {
  track?: IMicrophoneAudioTrack;
  level?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
};

export const AnimatedBar = ({ track, level = 0 }: AnimatedBarProps): JSX.Element => {
  const volumeLevel = useMotionValue(0);
  const scaleY = useTransform(volumeLevel, [0, 1], [1, 1 + 10 * Math.atan(level / 10)]);

  useEffect(() => {
    let volumeLevelTimeoutId: NodeJS.Timeout | undefined;

    if (track) {
      volumeLevelTimeoutId = setInterval(() => {
        const targetVolume = track.getVolumeLevel();

        animate(volumeLevel, targetVolume, { type: 'spring', stiffness: 400 });
      }, 50);
    } else {
      animate(volumeLevel, 0, { type: 'spring', duration: 0.7 });
    }

    return () => {
      if (volumeLevelTimeoutId) {
        clearInterval(volumeLevelTimeoutId);
      }
    };
  }, [track, volumeLevel]);

  return <motion.div className={styles.bar} style={{ width: 2, height: 2, scaleY }} />;
};

const AudioWave = ({ track }: AudioWaveProps): JSX.Element => (
  <div className={styles.container}>
    <AnimatedBar track={track} level={5} />
    <AnimatedBar track={track} level={10} />
    <AnimatedBar track={track} level={5} />
  </div>
);

export default memo(AudioWave);
